import React, { Fragment, useCallback, useEffect, useState } from "react";

import axios from "axios";
import Cookies from "universal-cookie";

import { FaCheck, FaClock, FaTimes, FaTrash } from "react-icons/fa";

import CreaDeclaracion from "./InfoDeclaracionCompromiso";
import { descargarDocumento } from "../Seguridad/DescargarDocumento";

const API_URL = "https://suadesarrollo.azurewebsites.net/api";

const AUTH = {
  username: "BasicAuth@icontec.org",
  password: "yQLzqmR7eZSuAmQZUqhY",
};

function TablaDeclaracion({ idSolicitud }) {
  const cookies = new Cookies();

  const tipoUsuario = Number(cookies.get("tipo_Usuario"));

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processingId, setProcessingId] = useState(null);

  /**
   * Obtener declaraciones
   */
  const getData = useCallback(async () => {
    if (!idSolicitud) return;

    try {
      setLoading(true);

      const response = await axios.get(
        `${API_URL}/ObtenerArchivoDeclaracionCompromisoPorSolicitudId`,
        {
          auth: AUTH,
          params: {
            SolicitudId: idSolicitud,
          },
        },
      );

      setList(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error al obtener declaraciones:", error);

      setList([]);
    } finally {
      setLoading(false);
    }
  }, [idSolicitud]);

  /**
   * Aprobar / rechazar declaración
   * Solo usuario tipo 1
   *
   * El administrador SIEMPRE puede aprobar o rechazar,
   * aunque la declaración ya haya sido revisada.
   */
  const handleAprobacion = useCallback(async (declaracionId, aprobacion) => {
    const accion = aprobacion ? "aprobar" : "rechazar";

    const confirmar = window.confirm(
      `¿Está seguro de que desea ${accion} esta declaración?`,
    );

    if (!confirmar) return;

    try {
      setProcessingId(declaracionId);

      await axios.put(
        `${API_URL}/ActualizaEstadoArchivoDeclaracionCompromiso/${declaracionId}/${aprobacion}`,
        {},
        {
          auth: AUTH,
        },
      );

      // Actualizar estado local
      setList((prev) =>
        prev.map((item) =>
          item.id === declaracionId
            ? {
                ...item,
                aprobada: aprobacion,
                aprobacionRevisada: true,
              }
            : item,
        ),
      );

      alert(
        aprobacion
          ? "Declaración aprobada correctamente."
          : "Declaración rechazada correctamente.",
      );
    } catch (error) {
      console.error(`Error al ${accion} declaración:`, error);

      alert(
        `No fue posible ${accion} la declaración. Por favor, intente nuevamente.`,
      );
    } finally {
      setProcessingId(null);
    }
  }, []);

  /**
   * Eliminar declaración
   * Solo usuario tipo 2
   */
  const handleDelete = useCallback(async (declaracionId) => {
    const confirmar = window.confirm(
      "¿Está seguro de que desea eliminar esta declaración?",
    );

    if (!confirmar) return;

    try {
      setProcessingId(declaracionId);

      await axios.delete(
        `${API_URL}/EliminarArchivoDeclaracionCompromisoPorId`,
        {
          auth: AUTH,
          params: {
            Id: declaracionId,
          },
        },
      );

      // Eliminar inmediatamente de la tabla
      setList((prev) => prev.filter((item) => item.id !== declaracionId));

      alert("Declaración eliminada correctamente.");
    } catch (error) {
      console.error("Error al eliminar declaración:", error);

      alert(
        "No fue posible eliminar la declaración. Por favor, intente nuevamente.",
      );
    } finally {
      setProcessingId(null);
    }
  }, []);

  /**
   * Estado visual de aprobación
   *
   * REGLAS:
   *
   * aprobacionRevisada = false
   *     -> Pendiente de revisión
   *
   * aprobacionRevisada = true
   * aprobada = true
   *     -> Aprobado
   *
   * aprobacionRevisada = true
   * aprobada = false
   *     -> Rechazado
   */
  const renderEstado = (item) => {
    if (item.aprobacionRevisada === false) {
      return (
        <span className="badge badge-warning">
          <FaClock className="mr-1" />
          Pendiente de revisión
        </span>
      );
    }

    if (item.aprobacionRevisada === true && item.aprobada === true) {
      return (
        <span className="badge badge-success">
          <FaCheck className="mr-1" />
          Aprobado
        </span>
      );
    }

    if (item.aprobacionRevisada === true && item.aprobada === false) {
      return (
        <span className="badge badge-danger">
          <FaTimes className="mr-1" />
          Rechazado
        </span>
      );
    }

    return <span className="badge badge-secondary">Estado no definido</span>;
  };

  /**
   * Acciones de aprobación
   *
   * IMPORTANTE:
   * El usuario tipo 1 SIEMPRE puede aprobar o rechazar.
   * No se bloquean los botones aunque aprobacionRevisada sea true.
   */
  const renderAccionesAprobacion = (item) => {
    const isProcessing = processingId === item.id;

    return (
      <div className="d-flex justify-content-center">
        <button
          type="button"
          className="btn btn-success btn-sm mr-2"
          disabled={isProcessing}
          onClick={() => handleAprobacion(item.id, true)}
        >
          <FaCheck className="mr-1" />

          {isProcessing ? "Procesando..." : "Aprobar"}
        </button>

        <button
          type="button"
          className="btn btn-danger btn-sm"
          disabled={isProcessing}
          onClick={() => handleAprobacion(item.id, false)}
        >
          <FaTimes className="mr-1" />

          {isProcessing ? "Procesando..." : "Rechazar"}
        </button>
      </div>
    );
  };

  /**
   * Carga inicial
   */
  useEffect(() => {
    getData();
  }, [getData]);

  /**
   * Mostrar columna de acciones
   *
   * Tipo 1 -> Aprobar / Rechazar
   * Tipo 2 -> Eliminar
   */
  const tieneAcciones = tipoUsuario === 1 || tipoUsuario === 2;

  return (
    <Fragment>
      <div className="text-center">
        <div className="container-fluid">
          <div className="card shadow mb-4">
            {/* HEADER */}
            <div className="card-header py-3">
              <h6 className="m-0 font-weight-bold text-primary">
                Declaración de Compromiso
              </h6>
            </div>

            {/* BODY */}
            <div className="card-body">
              {/* SOLO USUARIO TIPO 2 PUEDE CREAR */}
              {tipoUsuario === 2 && (
                <div className="mb-4">
                  <CreaDeclaracion
                    idSolicitud={idSolicitud}
                    onCreated={getData}
                  />
                </div>
              )}

              <div className="table-responsive">
                <table
                  className="table table-bordered table-hover"
                  width="100%"
                  cellSpacing="0"
                >
                  <thead>
                    <tr>
                      <th>Id</th>
                      <th>Documento</th>
                      <th>Solicitud</th>
                      <th>Estado</th>

                      {tieneAcciones && <th>Acciones</th>}
                    </tr>
                  </thead>

                  <tbody>
                    {/* CARGANDO */}
                    {loading ? (
                      <tr>
                        <td
                          colSpan={tieneAcciones ? 5 : 4}
                          className="text-center"
                        >
                          <div
                            className="spinner-border text-primary"
                            role="status"
                          >
                            <span className="sr-only">Cargando...</span>
                          </div>

                          <div className="mt-2">Cargando declaraciones...</div>
                        </td>
                      </tr>
                    ) : list.length === 0 ? (
                      /* SIN INFORMACIÓN */
                      <tr>
                        <td
                          colSpan={tieneAcciones ? 5 : 4}
                          className="text-center text-muted"
                        >
                          No existen declaraciones registradas.
                        </td>
                      </tr>
                    ) : (
                      /* INFORMACIÓN */
                      list.map((item) => {
                        const isProcessing = processingId === item.id;

                        return (
                          <tr key={item.id}>
                            {/* ID */}
                            <td>{item.id}</td>

                            {/* DOCUMENTO */}
                            <td>
                              {item.ubicacion ? (
                                <button
                                  type="button"
                                  className="btn btn-success btn-sm"
                                  onClick={() =>
                                    descargarDocumento(item.ubicacion)
                                  }
                                >
                                  Descargar Documento
                                </button>
                              ) : (
                                <span className="text-muted">
                                  Sin documento
                                </span>
                              )}
                            </td>

                            {/* SOLICITUD */}
                            <td>{item.solicitudId}</td>

                            {/* ESTADO */}
                            <td>{renderEstado(item)}</td>

                            {/* ACCIONES */}
                            {tieneAcciones && (
                              <td className="text-center">
                                {/* TIPO 1:
                                    SIEMPRE PUEDE APROBAR / RECHAZAR */}
                                {tipoUsuario === 1 &&
                                  renderAccionesAprobacion(item)}

                                {/* TIPO 2:
                                    SOLO ELIMINAR */}
                                {tipoUsuario === 2 && (
                                  <button
                                    type="button"
                                    className="btn btn-danger btn-sm"
                                    title="Eliminar declaración"
                                    disabled={isProcessing}
                                    onClick={() => handleDelete(item.id)}
                                  >
                                    <FaTrash className="mr-1" />

                                    {isProcessing
                                      ? "Eliminando..."
                                      : "Eliminar"}
                                  </button>
                                )}
                              </td>
                            )}
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
}

export default TablaDeclaracion;
